import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Alert,
  TextField,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
} from '@mui/material';
import {
  SmartToy as AiIcon,
  FitnessCenter as FitnessIcon,
  Send as SendIcon,
  Add as AddIcon,
  Check as CheckIcon,
  ExpandMore as ExpandMoreIcon,
  AutoAwesome as AutoAwesomeIcon,
  Build as BuildIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { AiService } from '../services/aiService';
import type {
  AiSuggestedExercise,
  AiSuggestionsResult,
  AiBuildProgramResult,
  AiProgramDaySuggestion,
} from '../services/aiService';
import { TrainingProgramService } from '../services/trainingProgramService';
import { ProgramExerciseService } from '../services/programExerciseService';
import type { TrainingProgram, ProgramExercisesByDay, ProgramDayNames } from '../services/types';

type BuilderMode = 'suggest' | 'build';

interface AppliedExercise {
  exerciseId: number;
  dayNumber: number;
}

const PROGRAM_EXERCISES_UPDATED_EVENT = 'program-exercises-updated';

const AiProgramBuilder: React.FC = () => {
  // Program state
  const [programs, setPrograms] = useState<TrainingProgram[]>([]);
  const [selectedProgramId, setSelectedProgramId] = useState<number | ''>('');
  const [programLoading, setProgramLoading] = useState(true);
  const [currentExercises, setCurrentExercises] = useState<ProgramExercisesByDay>({});
  const [dayNames, setDayNames] = useState<ProgramDayNames>({});

  // Mode state
  const [mode, setMode] = useState<BuilderMode>('suggest');

  // Suggest mode state
  const [suggestDayNumber, setSuggestDayNumber] = useState<number | ''>('');
  const [suggestMessage, setSuggestMessage] = useState('');
  const [suggestLoading, setSuggestLoading] = useState(false);
  const [suggestions, setSuggestions] = useState<AiSuggestionsResult | null>(null);
  const [suggestRawResponse, setSuggestRawResponse] = useState<string | null>(null);

  // Build mode state
  const [splitType, setSplitType] = useState('');
  const [buildGoal, setBuildGoal] = useState('');
  const [buildMessage, setBuildMessage] = useState('');
  const [buildLoading, setBuildLoading] = useState(false);
  const [buildResult, setBuildResult] = useState<AiBuildProgramResult | null>(null);
  const [buildRawResponse, setBuildRawResponse] = useState<string | null>(null);

  // Apply state
  const [applying, setApplying] = useState(false);
  const [appliedExercises, setAppliedExercises] = useState<AppliedExercise[]>([]);
  const [applyError, setApplyError] = useState<string | null>(null);

  useEffect(() => {
    loadPrograms();
  }, []);

  useEffect(() => {
    if (selectedProgramId) {
      loadProgramExercises(selectedProgramId as number);
    }
  }, [selectedProgramId]);

  const loadPrograms = async () => {
    try {
      setProgramLoading(true);
      const allPrograms = await TrainingProgramService.getAll();
      setPrograms(allPrograms);
      // Auto-select active program
      const active = allPrograms.find(p => p.active);
      if (active) {
        setSelectedProgramId(active.id);
      }
    } catch {
      // ignore
    } finally {
      setProgramLoading(false);
    }
  };

  const loadProgramExercises = async (programId: number) => {
    try {
      const data = await ProgramExerciseService.getProgramExercises(programId);
      setCurrentExercises(data.exercisesByDay || {});
      setDayNames(data.dayNames || {});
    } catch {
      // ignore
    }
  };

  const handleSuggestExercises = async () => {
    if (!selectedProgramId) return;

    try {
      setSuggestLoading(true);
      setSuggestions(null);
      setSuggestRawResponse(null);
      setAppliedExercises([]);
      setApplyError(null);

      const result = await AiService.suggestExercises({
        programId: selectedProgramId as number,
        dayNumber: suggestDayNumber !== '' ? (suggestDayNumber as number) : undefined,
        message: suggestMessage || undefined,
      });

      setSuggestRawResponse(result.response);
      const parsed = AiService.parseExerciseSuggestions(result.response);
      setSuggestions(parsed);
    } catch (err: any) {
      setSuggestRawResponse('Грешка: ' + (err.message || 'AI услугата не е достъпна'));
    } finally {
      setSuggestLoading(false);
    }
  };

  const handleBuildProgram = async () => {
    if (!selectedProgramId) return;

    try {
      setBuildLoading(true);
      setBuildResult(null);
      setBuildRawResponse(null);
      setAppliedExercises([]);
      setApplyError(null);

      const result = await AiService.buildProgram({
        programId: selectedProgramId as number,
        splitType: splitType || undefined,
        goal: buildGoal || undefined,
        message: buildMessage || undefined,
      });

      setBuildRawResponse(result.response);
      const parsed = AiService.parseBuildProgramResult(result.response);
      setBuildResult(parsed);
    } catch (err: any) {
      setBuildRawResponse('Грешка: ' + (err.message || 'AI услугата не е достъпна'));
    } finally {
      setBuildLoading(false);
    }
  };

  const handleApplySuggestion = async (exercise: AiSuggestedExercise) => {
    if (!selectedProgramId) return;
    const alreadyApplied = appliedExercises.some(
      a => a.exerciseId === exercise.exerciseId && a.dayNumber === exercise.dayNumber
    );
    if (alreadyApplied) return;

    try {
      setApplying(true);
      setApplyError(null);
      await ProgramExerciseService.addExerciseToProgram(selectedProgramId as number, {
        exerciseId: exercise.exerciseId,
        dayOfWeek: exercise.dayNumber,
        targetSets: exercise.sets,
        targetReps: exercise.reps,
      });
      setAppliedExercises(prev => [...prev, { exerciseId: exercise.exerciseId, dayNumber: exercise.dayNumber }]);
      await loadProgramExercises(selectedProgramId as number);
      window.dispatchEvent(new CustomEvent(PROGRAM_EXERCISES_UPDATED_EVENT));
    } catch (err: any) {
      setApplyError('Грешка при добавяне: ' + (err.message || 'Неизвестна грешка'));
    } finally {
      setApplying(false);
    }
  };

  const handleApplyFullDay = async (day: AiProgramDaySuggestion) => {
    if (!selectedProgramId) return;

    try {
      setApplying(true);
      setApplyError(null);
      for (const exercise of (day.exercises ?? [])) {
        const alreadyApplied = appliedExercises.some(
          a => a.exerciseId === exercise.exerciseId && a.dayNumber === day.dayNumber
        );
        if (alreadyApplied) continue;

        await ProgramExerciseService.addExerciseToProgram(selectedProgramId as number, {
          exerciseId: exercise.exerciseId,
          dayOfWeek: day.dayNumber,
          targetSets: exercise.sets,
          targetReps: exercise.reps,
        });
        setAppliedExercises(prev => [...prev, { exerciseId: exercise.exerciseId, dayNumber: day.dayNumber }]);
      }
      await loadProgramExercises(selectedProgramId as number);
      window.dispatchEvent(new CustomEvent(PROGRAM_EXERCISES_UPDATED_EVENT));
    } catch (err: any) {
      setApplyError('Грешка при добавяне: ' + (err.message || 'Неизвестна грешка'));
    } finally {
      setApplying(false);
    }
  };

  const isExerciseApplied = (exerciseId: number, dayNumber: number) => {
    return appliedExercises.some(a => a.exerciseId === exerciseId && a.dayNumber === dayNumber);
  };

  const selectedProgram = programs.find(p => p.id === selectedProgramId);
  const dayNumbers = Object.keys(currentExercises).map(Number).sort((a, b) => a - b);
  // Include days from dayNames that may not have exercises yet
  const allDayNumbers = Array.from(new Set([
    ...dayNumbers,
    ...Object.keys(dayNames).map(Number),
  ])).sort((a, b) => a - b);

  const renderCurrentProgram = () => {
    if (!selectedProgramId || allDayNumbers.length === 0) return null;

    return (
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          Текуща структура на програмата:
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {allDayNumbers.map(day => {
            const exercises = currentExercises[day] || [];
            const name = dayNames[day] || `Ден ${day}`;
            return (
              <Chip
                key={day}
                label={`${name}: ${exercises.length} упр.`}
                size="small"
                variant="outlined"
                color={exercises.length > 0 ? 'primary' : 'default'}
              />
            );
          })}
        </Box>
      </Box>
    );
  };

  const renderSuggestion = (exercise: AiSuggestedExercise, index: number) => {
    const applied = isExerciseApplied(exercise.exerciseId, exercise.dayNumber);
    const dn = dayNames[exercise.dayNumber] || `Ден ${exercise.dayNumber}`;

    return (
      <ListItem
        key={`${exercise.exerciseId}-${exercise.dayNumber}-${index}`}
        sx={{
          bgcolor: applied ? 'action.selected' : 'transparent',
          borderRadius: 1,
          mb: 0.5,
        }}
        secondaryAction={
          <Tooltip title={applied ? 'Добавено' : 'Добави към програмата'}>
            <span>
              <IconButton
                edge="end"
                color={applied ? 'success' : 'primary'}
                onClick={() => handleApplySuggestion(exercise)}
                disabled={applied || applying}
                size="small"
              >
                {applied ? <CheckIcon /> : <AddIcon />}
              </IconButton>
            </span>
          </Tooltip>
        }
      >
        <ListItemIcon>
          <FitnessIcon color="primary" />
        </ListItemIcon>
        <ListItemText
          primary={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="body1" fontWeight="medium">
                {exercise.exerciseName}
              </Typography>
              <Chip label={`${exercise.sets}x${exercise.reps}`} size="small" color="primary" variant="outlined" />
              <Chip label={dn} size="small" variant="outlined" />
            </Box>
          }
          secondary={exercise.reason}
        />
      </ListItem>
    );
  };

  const renderBuildDaySuggestion = (day: AiProgramDaySuggestion) => {
    const exercises = day.exercises ?? [];
    const allApplied = exercises.length > 0 && exercises.every(ex => isExerciseApplied(ex.exerciseId, day.dayNumber));

    return (
      <Accordion key={day.dayNumber} defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', mr: 1 }}>
            <Typography fontWeight="bold">
              Ден {day.dayNumber}: {day.dayName}
            </Typography>
            <Chip label={`${exercises.length} упр.`} size="small" color="primary" />
            {!allApplied && (
              <Button
                size="small"
                variant="outlined"
                startIcon={applying ? <CircularProgress size={16} /> : <AddIcon />}
                onClick={(e) => {
                  e.stopPropagation();
                  handleApplyFullDay(day);
                }}
                disabled={applying || allApplied}
                sx={{ ml: 'auto' }}
              >
                Добави всички
              </Button>
            )}
            {allApplied && (
              <Chip label="Добавен" color="success" size="small" sx={{ ml: 'auto' }} icon={<CheckIcon />} />
            )}
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <List dense disablePadding>
            {exercises.map((exercise, idx) => {
              const applied = isExerciseApplied(exercise.exerciseId, day.dayNumber);
              return (
                <ListItem
                  key={`${exercise.exerciseId}-${idx}`}
                  sx={{
                    bgcolor: applied ? 'action.selected' : 'transparent',
                    borderRadius: 1,
                    mb: 0.5,
                  }}
                  secondaryAction={
                    <Tooltip title={applied ? 'Добавено' : 'Добави'}>
                      <span>
                        <IconButton
                          edge="end"
                          color={applied ? 'success' : 'primary'}
                          onClick={() => handleApplySuggestion({
                            exerciseId: exercise.exerciseId,
                            exerciseName: exercise.exerciseName,
                            reason: '',
                            sets: exercise.sets,
                            reps: exercise.reps,
                            dayNumber: day.dayNumber,
                          })}
                          disabled={applied || applying}
                          size="small"
                        >
                          {applied ? <CheckIcon /> : <AddIcon />}
                        </IconButton>
                      </span>
                    </Tooltip>
                  }
                >
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <Typography variant="body2" color="text.secondary">
                      {exercise.order}.
                    </Typography>
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2">{exercise.exerciseName}</Typography>
                        <Chip label={`${exercise.sets}x${exercise.reps}`} size="small" variant="outlined" />
                      </Box>
                    }
                  />
                </ListItem>
              );
            })}
          </List>
        </AccordionDetails>
      </Accordion>
    );
  };

  return (
    <Box>
      {/* Program selection */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            <AiIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            AI Програмен съветник
          </Typography>

          {programLoading ? (
            <CircularProgress size={24} />
          ) : (
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel>Избери програма</InputLabel>
              <Select
                value={selectedProgramId}
                label="Избери програма"
                onChange={(e) => {
                  setSelectedProgramId(e.target.value as number);
                  setSuggestions(null);
                  setBuildResult(null);
                  setSuggestRawResponse(null);
                  setBuildRawResponse(null);
                  setAppliedExercises([]);
                }}
              >
                {programs.map(p => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.name} {p.active ? '(активна)' : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {selectedProgram && (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
              <Chip label={`Цел: ${selectedProgram.goal}`} size="small" variant="outlined" />
              <Chip label={`Трудност: ${selectedProgram.difficulty}`} size="small" variant="outlined" />
              <Chip label={`${selectedProgram.workoutsPerWeek} тренировки/седмица`} size="small" variant="outlined" />
            </Box>
          )}

          {renderCurrentProgram()}

          {/* Mode selection */}
          {selectedProgramId && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant={mode === 'suggest' ? 'contained' : 'outlined'}
                startIcon={<AutoAwesomeIcon />}
                onClick={() => setMode('suggest')}
                size="small"
              >
                Предложи упражнения
              </Button>
              <Button
                variant={mode === 'build' ? 'contained' : 'outlined'}
                startIcon={<BuildIcon />}
                onClick={() => setMode('build')}
                size="small"
              >
                Изгради програма
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Suggest exercises mode */}
      {selectedProgramId && mode === 'suggest' && (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="subtitle1" gutterBottom fontWeight="bold">
              <AutoAwesomeIcon sx={{ mr: 1, verticalAlign: 'middle', fontSize: 20 }} />
              Предложи упражнения
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              AI ще анализира програмата ти и ще предложи упражнения от базата данни.
            </Typography>

            <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>За ден (опция)</InputLabel>
                <Select
                  value={suggestDayNumber}
                  label="За ден (опция)"
                  onChange={(e) => setSuggestDayNumber(e.target.value as number | '')}
                >
                  <MenuItem value="">Всички дни</MenuItem>
                  {allDayNumbers.map(day => (
                    <MenuItem key={day} value={day}>
                      {dayNames[day] || `Ден ${day}`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Допълнителни инструкции (напр. 'повече упражнения за гърди', 'фокус върху хипертрофия')..."
                value={suggestMessage}
                onChange={(e) => setSuggestMessage(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSuggestExercises();
                  }
                }}
                disabled={suggestLoading}
              />
              <Button
                variant="contained"
                onClick={handleSuggestExercises}
                disabled={suggestLoading}
                startIcon={suggestLoading ? <CircularProgress size={20} /> : <SendIcon />}
                sx={{ minWidth: 120 }}
              >
                {suggestLoading ? 'Мисля...' : 'Предложи'}
              </Button>
            </Box>

            {/* Suggestions result */}
            {suggestions && (
              <Paper sx={{ mt: 2, p: 2, bgcolor: 'background.default' }}>
                {suggestions.explanation && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    {suggestions.explanation}
                  </Alert>
                )}
                {applyError && (
                  <Alert severity="error" sx={{ mb: 1 }}>
                    {applyError}
                  </Alert>
                )}
                <List dense>
                  {suggestions.suggestions.map((ex, i) => renderSuggestion(ex, i))}
                </List>
              </Paper>
            )}

            {/* Raw response fallback */}
            {suggestRawResponse && !suggestions && !suggestLoading && (
              <Paper sx={{ mt: 2, p: 2, bgcolor: 'background.default' }}>
                <Typography variant="subtitle2" color="warning.main" gutterBottom>
                  AI отговор (не успях да парсна структуриран отговор):
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {suggestRawResponse}
                </Typography>
                <Button
                  size="small"
                  startIcon={<RefreshIcon />}
                  onClick={handleSuggestExercises}
                  sx={{ mt: 1 }}
                >
                  Опитай отново
                </Button>
              </Paper>
            )}
          </CardContent>
        </Card>
      )}

      {/* Build program mode */}
      {selectedProgramId && mode === 'build' && (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="subtitle1" gutterBottom fontWeight="bold">
              <BuildIcon sx={{ mr: 1, verticalAlign: 'middle', fontSize: 20 }} />
              Изгради цялата програма
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              AI ще генерира пълна тренировъчна програма с разпределение по дни.
            </Typography>

            <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>Тип сплит (опция)</InputLabel>
                <Select
                  value={splitType}
                  label="Тип сплит (опция)"
                  onChange={(e) => setSplitType(e.target.value)}
                >
                  <MenuItem value="">Автоматично</MenuItem>
                  <MenuItem value="push/pull/legs">Push/Pull/Legs</MenuItem>
                  <MenuItem value="upper/lower">Upper/Lower</MenuItem>
                  <MenuItem value="full body">Full Body</MenuItem>
                  <MenuItem value="bro split">Bro Split</MenuItem>
                  <MenuItem value="push/pull/legs/upper/lower">PPL + Upper/Lower</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Цел (опция)</InputLabel>
                <Select
                  value={buildGoal}
                  label="Цел (опция)"
                  onChange={(e) => setBuildGoal(e.target.value)}
                >
                  <MenuItem value="">От програмата</MenuItem>
                  <MenuItem value="hypertrophy">Хипертрофия</MenuItem>
                  <MenuItem value="strength">Сила</MenuItem>
                  <MenuItem value="endurance">Издръжливост</MenuItem>
                  <MenuItem value="weight_loss">Отслабване</MenuItem>
                  <MenuItem value="general_fitness">Обща форма</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Допълнителни инструкции (напр. 'без кардио', 'повече задна верига')..."
                value={buildMessage}
                onChange={(e) => setBuildMessage(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleBuildProgram();
                  }
                }}
                disabled={buildLoading}
              />
              <Button
                variant="contained"
                onClick={handleBuildProgram}
                disabled={buildLoading}
                startIcon={buildLoading ? <CircularProgress size={20} /> : <BuildIcon />}
                sx={{ minWidth: 120 }}
              >
                {buildLoading ? 'Генериране...' : 'Генерирай'}
              </Button>
            </Box>

            {buildLoading && (
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <CircularProgress size={40} />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  AI генерира програмата... Това може да отнеме до 2 минути.
                </Typography>
              </Box>
            )}

            {/* Build result */}
            {buildResult && (
              <Paper sx={{ mt: 2, p: 2, bgcolor: 'background.default' }}>
                {buildResult.splitDescription && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    {buildResult.splitDescription}
                  </Alert>
                )}
                {applyError && (
                  <Alert severity="error" sx={{ mb: 1 }}>
                    {applyError}
                  </Alert>
                )}
                {buildResult.days.map(day => renderBuildDaySuggestion(day))}
              </Paper>
            )}

            {/* Raw response fallback */}
            {buildRawResponse && !buildResult && !buildLoading && (
              <Paper sx={{ mt: 2, p: 2, bgcolor: 'background.default' }}>
                <Typography variant="subtitle2" color="warning.main" gutterBottom>
                  AI отговор (не успях да парсна структуриран отговор):
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {buildRawResponse}
                </Typography>
                <Button
                  size="small"
                  startIcon={<RefreshIcon />}
                  onClick={handleBuildProgram}
                  sx={{ mt: 1 }}
                >
                  Опитай отново
                </Button>
              </Paper>
            )}
          </CardContent>
        </Card>
      )}

      {/* No program selected message */}
      {!selectedProgramId && !programLoading && (
        <Alert severity="info">
          Моля, изберете програма, за да започнете.
        </Alert>
      )}
    </Box>
  );
};

export default AiProgramBuilder;
